import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';

import { TRestaurant, getRestaurantDisplayName } from '../schemas/restaurant-schema';

type RestaurantSelectorProps = {
  restaurants: TRestaurant[];
  selectedRestaurant: TRestaurant | null;
  onRestaurantChange: (restaurant: TRestaurant | null) => void;
  isLoading?: boolean;
};

const RestaurantSelector = ({
  restaurants,
  selectedRestaurant,
  onRestaurantChange,
  isLoading = false,
}: RestaurantSelectorProps) => {
  return (
    <Box sx={styles.container}>
      <Typography sx={styles.label}>Select Restaurant</Typography>
      <Autocomplete
        options={restaurants}
        getOptionLabel={(option) => {
          if (!option) return '';
          return getRestaurantDisplayName(option);
        }}
        value={selectedRestaurant}
        onChange={(_, newValue) => onRestaurantChange(newValue)}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...(params as any)}
            placeholder="Choose a restaurant..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={styles.textField}
          />
        )}
        sx={styles.autocomplete}
        renderOption={(props, option) => {
          const displayName = getRestaurantDisplayName(option);
          const avatarLetter = displayName[0]?.toUpperCase() || 'R';

          return (
            <Box component="li" {...props} sx={styles.option}>
              <Avatar
                src={option.image || option.logo || ''}
                alt={displayName}
                sx={styles.avatar}
              >
                {avatarLetter}
              </Avatar>
              <Typography sx={styles.optionTitle}>
                {displayName}
              </Typography>
            </Box>
          );
        }}
        noOptionsText="No restaurants found"
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option._id === value._id;
        }}
      />
    </Box>
  );
};

export default RestaurantSelector;

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
    marginBottom: 3,
  },
  label: {
    fontFamily: 'GilroyBold',
    fontSize: 14,
    color: '#333',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  autocomplete: {
    minWidth: '300px',
    maxWidth: '500px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'GilroyRegular',
      fontSize: 14,
      borderRadius: '8px',
      backgroundColor: '#fff',
    },
  },
  option: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: 1.5,
    padding: '10px 16px',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  avatar: {
    width: 36,
    height: 36,
    fontSize: 14,
    fontFamily: 'GilroyBold',
    backgroundColor: '#000',
  },
  optionTitle: {
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
    color: '#1a1a1a',
    textAlign: 'left' as const,
  },
};
